import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useNotification, useTranslation } from "@refinedev/core";

import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";

import { CardButton } from "@/components/common/CardButton";
import { ClinicOption } from "@/types/clinic-types";
import {
  BROADCAST_MESSAGE_MUTATION,
  BroadcastChatAnnouncementInput,
  BroadcastChatAnnouncementResponse,
  LIST_ORG_CHAT_ROOMS_QUERY,
  ListChatRoomsInput,
  ListChatRoomsResponse,
} from "@/utils/GraphQLClient/messenger";

import * as Sentry from "@sentry/react";
import { useMutation, useQuery } from "urql";

import { MessageBroadcastModalFields } from "./MessageBroadcastModalFields";

interface MessageBroadcastModalProps {
  open: boolean;
  onClose: () => void;
}

export enum BroadcastMessageFilterEnum {
  ALL = "all", // Find all active chat rooms within the org
  CLINIC = "clinic", // Filter by clinics -> optionally filter by pracs
  PRACTITIONER = "practitioner", // Filters by practitioners -> optionally filter by clinics
}

export type BroadcastMessageFormValues = {
  filter: BroadcastMessageFilterEnum | null;
  clinic_ids?: Pick<ClinicOption, "id" | "label" | "value">[];
  practitioner_ids?: { id: string; label: string; value: string; clinicNames?: string[]; clinicIds?: string[] }[];
  message: string;
  sender: string;
  chat_room_count: number | null;
  chat_room_loading: boolean;
};

const defaultFormValues = {
  filter: null,
  clinic_ids: [],
  practitioner_ids: [],
  message: "",
  sender: "",
  chat_room_count: null,
  chat_room_loading: false,
};

export const MessageBroadcastModal: React.FC<MessageBroadcastModalProps> = ({ open, onClose }) => {
  const { translate } = useTranslation();
  const { open: openNotification } = useNotification();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [chatRoomQueryError, setChatRoomQueryError] = useState<string | null>(null);

  const methods = useForm<BroadcastMessageFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
    defaultValues: defaultFormValues,
  });
  const { formState, getValues, reset, setValue, trigger, watch } = methods;
  const [numOfChatRooms, loadingRooms] = watch(["chat_room_count", "chat_room_loading"]);

  const [{ data: chatRoomsData, error: chatRoomsError, fetching: isChatRoomLoading }, reExecuteChatRoomsQuery] =
    useQuery<ListChatRoomsResponse, { input: ListChatRoomsInput }>({
      query: LIST_ORG_CHAT_ROOMS_QUERY,
      variables: {
        input: (() => {
          const [filterValue, clinicIdValues = [], practitionerIdValues = []] = getValues([
            "filter",
            "clinic_ids",
            "practitioner_ids",
          ]);
          if (filterValue === BroadcastMessageFilterEnum.ALL) {
            return {};
          }
          return {
            ...(clinicIdValues.length > 0 && {
              fhir_clinic_ids: clinicIdValues.map(({ value }) => value),
            }),
            ...(practitionerIdValues.length > 0 && {
              fhir_practitioner_ids: practitionerIdValues.map(({ value }) => value),
            }),
          };
        })(),
      },
      pause: true, // Prevents automatic execution on mount
    });

  const activeOrgChatRooms = chatRoomsData?.listChatRooms ?? [];
  const loading = isChatRoomLoading || loadingRooms;

  // Keep a ref to the latest reExecute so the 2 s timeout always uses the
  // version bound to the current query variables (input: {} vs clinic-filtered).
  const reExecuteQueryRef = useRef(reExecuteChatRoomsQuery);
  reExecuteQueryRef.current = reExecuteChatRoomsQuery;

  // Track the last processed filter value to prevent duplicate watch
  // notifications from MUI Autocomplete's controlled value reconciliation.
  const prevFilterRef = useRef<BroadcastMessageFilterEnum | null>(null);

  const handleChatRoomQuery = useCallback(() => {
    setValue("chat_room_loading", true);
    setTimeout(() => {
      reExecuteQueryRef.current({ requestPolicy: "network-only" });
    }, 2000);
  }, [setValue]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "filter" && value.filter !== prevFilterRef.current) {
        prevFilterRef.current = value.filter ?? null;
        setValue("chat_room_count", null);
        setValue("clinic_ids", []);
        setValue("practitioner_ids", []);
        switch (value.filter) {
          case BroadcastMessageFilterEnum.ALL:
            // Defer query execution to a microtask so React commits the
            // setValue state updates before urql's execute triggers its own
            // render cycle. Running both synchronously causes the batched
            // render to short-circuit urql's useEffect subscription.
            queueMicrotask(() => handleChatRoomQuery());
            break;

          case BroadcastMessageFilterEnum.CLINIC:
          case BroadcastMessageFilterEnum.PRACTITIONER:
          default:
            break;
        }
      }
      /**
       * Scenario: Select clinic/practitioner filters and click 'Apply filters'. Fill out sender name and message.
       * Update practitioner/clinic filters. The chat rooms needs to be updated to reflect the form state.
       * Reset chat_room_count to null to hide the sender and message inputs. Forces user to click 'Apply Filters' again
       */
      if ((name === "clinic_ids" || name === "practitioner_ids") && typeof value.chat_room_count === "number") {
        setValue("chat_room_count", null);
      }
    });

    return () => subscription.unsubscribe();
  }, [handleChatRoomQuery, setValue, watch]);

  useEffect(() => {
    if (!isChatRoomLoading && chatRoomsData) {
      setValue("chat_room_count", activeOrgChatRooms.length);
      setValue("chat_room_loading", false);
    }

    if (!chatRoomsError) {
      return;
    }
    Sentry.captureException(chatRoomsError, {
      level: "error",
      tags: {
        component: "MessageBroadcastModal.tsx",
        feature: "messenger:broadcast-message",
        operation: "LIST_ORG_CHAT_ROOMS_QUERY",
      },
      extra: { formValues: JSON.stringify(getValues()) },
    });
    const errorMessage =
      chatRoomsError.graphQLErrors?.[0]?.message ??
      chatRoomsError.message ??
      translate("messenger.broadcastMessage.roomQueryError");
    setChatRoomQueryError(errorMessage);
    setValue("chat_room_loading", false);
  }, [activeOrgChatRooms.length, chatRoomsData, chatRoomsError, getValues, isChatRoomLoading, setValue, translate]);

  const [, sendSystemMessage] = useMutation<
    BroadcastChatAnnouncementResponse,
    { input: BroadcastChatAnnouncementInput }
  >(BROADCAST_MESSAGE_MUTATION);

  const validateInputs = async () => {
    return trigger(["sender", "message", "clinic_ids", "practitioner_ids"]);
  };

  const handleClose = () => {
    reset();
    prevFilterRef.current = null;
    onClose();
    setChatRoomQueryError(null);
  };

  const handleMessageBroadcast = async () => {
    const ok = await validateInputs();
    if (!ok) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formValues = getValues();
      const { data, error } = await sendSystemMessage({
        input: {
          message: formValues.message,
          sender_name: formValues.sender,
          chat_room_ids: activeOrgChatRooms.map(({ name }) => name),
        },
      });
      if (error) {
        throw error;
      }

      const result = data?.broadcastChatAnnouncement;
      if (result && result.failed === 0) {
        openNotification?.({
          type: "success",
          message: translate("messenger.broadcastMessage.success"),
        });
      } else {
        Sentry.captureException("Broadcast msg: Partial success", {
          level: "error",
          tags: { feature: "messenger", operation: "BROADCAST_MESSAGE_MUTATION" },
          extra: { error: JSON.stringify({ data, error }) },
        });
        openNotification?.({
          type: "error",
          message: translate("messenger.broadcastMessage.partialFailure", {
            queued: result?.queued,
            total: result?.total,
            failed: result?.failed,
          }),
        });
      }
    } catch (e) {
      Sentry.captureException("Broadcast msg: Cannot enqueue", {
        level: "error",
        tags: { feature: "messenger", operation: "BROADCAST_MESSAGE_MUTATION" },
        extra: { error: JSON.stringify(e) },
      });
      openNotification?.({
        type: "error",
        message: translate("messenger.broadcastMessage.error"),
      });
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleClose();
        }}
        maxWidth="md"
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h3" color="text.primary">
              {translate("messenger.broadcastMessage.title")}
            </Typography>
            <IconButton disabled={isSubmitting} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="subtitle1" color="text.primary">
            {translate("messenger.broadcastMessage.description")}
          </Typography>

          <Divider sx={{ mx: 2, mt: 1, mb: 1 }} />

          <MessageBroadcastModalFields chatRoomLoading={loading} onQueryForChatRooms={handleChatRoomQuery} />
          {chatRoomQueryError && <Typography color="error">{chatRoomQueryError}</Typography>}
        </DialogContent>

        {(numOfChatRooms ?? 0) > 0 && (
          <DialogActions sx={{ justifyContent: "flex-end", px: 3, py: 2 }}>
            <Box>
              <CardButton
                data-testid="message-broadcast-submit-button"
                endIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
                variant="outlined"
                onClick={handleMessageBroadcast}
                disabled={isSubmitting || Boolean(!numOfChatRooms) || loading || !formState.isValid}
                fullWidth={false}
                height={35}
              >
                {translate("messenger.broadcastMessage.send", {
                  count: numOfChatRooms,
                })}
              </CardButton>
            </Box>
          </DialogActions>
        )}
      </Dialog>
    </FormProvider>
  );
};
